import type React from "react";
import { cn } from "@/lib/utils";

/**
 * The page rhythm alternates three grounds: `night` (dark theme, for the hero,
 * the proof and the final call), `paper` and `stone` (a quiet tinted paper).
 */
export type SectionTone = "paper" | "stone" | "night";

const toneClass: Record<SectionTone, string> = {
	paper: "bg-background text-foreground",
	stone: "bg-muted/40 text-foreground",
	night: "dark bg-background text-foreground",
};

/** Page section with the site's standard width and rhythm. */
export function Section({
	id,
	tone = "paper",
	className,
	containerClassName,
	decor,
	children,
}: {
	id?: string;
	tone?: SectionTone;
	className?: string;
	containerClassName?: string;
	/** Absolutely positioned background layer (grid, glow). */
	decor?: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<section
			id={id}
			className={cn(
				"relative isolate scroll-mt-16 overflow-clip",
				toneClass[tone],
				className,
			)}
		>
			{decor ? (
				<div aria-hidden="true" className="absolute inset-0 -z-10">
					{decor}
				</div>
			) : null}
			<div
				className={cn(
					"mx-auto w-full max-w-7xl px-6 py-20 md:py-28 lg:px-10",
					containerClassName,
				)}
			>
				{children}
			</div>
		</section>
	);
}
